import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from '@/utils/constants';

interface SocketState {
  socket: Socket | null;
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
}

export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  isConnected: false,
  connect: () => {
    // WebSocket is disabled by default. Set VITE_ENABLE_WEBSOCKET=true to enable.
    const enableWebSocket = import.meta.env.VITE_ENABLE_WEBSOCKET === 'false';
    if (!enableWebSocket) {
      // Silently skip - don't log to avoid console noise
      return;
    }
    
    if (get().socket) return;
    
    try {
      const base = API_BASE_URL.replace(/\/?api\/?$/, '');
      const token = localStorage.getItem('token');
      
      const socket = io(base, { 
        transports: ['websocket'], 
        path: '/ws/socket.io',
        auth: token ? { token } : undefined,
        reconnection: false,
      });
      
      socket.on('connect', () => {
        set({ isConnected: true });
      });
      
      socket.on('disconnect', () => {
        set({ isConnected: false });
      });
      
      socket.on('connect_error', (error) => {
        console.warn('WebSocket connection error:', error.message);
        set({ isConnected: false });
      });
      
      set({ socket });
    } catch (error) {
      console.warn('Failed to initialize WebSocket:', error);
    }
  },
  disconnect: () => {
    const s = get().socket;
    if (!s) return;
    s.disconnect();
    set({ socket: null, isConnected: false });
  },
  joinRoom: (room: string) => {
    // Only join if socket exists and is connected
    const s = get().socket;
    if (!s || !get().isConnected) return;
    try {
      s.emit('join', room);
    } catch (error) {
      // Silently fail if socket operations fail
    }
  },
  leaveRoom: (room: string) => {
    // Only leave if socket exists
    const s = get().socket;
    if (!s) return;
    try {
      s.emit('leave', room);
    } catch (error) {
      // Silently fail if socket operations fail
    }
  },
}));
